import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import { Box, Button, Drawer, Tooltip, Typography } from '@mui/material'
import { Fragment, useEffect, useReducer, useState } from 'react'

import { PromptDialog } from '@/components'
import { useAppContext } from '@/hooks'

import { DiffEngine, DiffNode } from '../diffEngine'
import type { MrStructure } from '../types'
import {
	FAM_COLOR,
	FAM_GLYPH,
	buildTree,
	collectionRows,
	collections,
	conflictNodes,
	entityFields,
	fmtValue,
	statusFamily,
	topAttrRows,
	unresolvedCount,
} from '../mrHelpers'

const DRAWER_MIN_W = 640
const DRAWER_DEFAULT_W = 1180
const drawerMaxW = () => Math.round(window.innerWidth * 0.96)
const HEAD = 40

type Row = { node: DiffNode; type: 'leaf' | 'block' | 'entity' }
type Resolution = 'source' | 'target' | 'custom'

type Props = {
	open: boolean
	structure: MrStructure | null
	sourceBranchName?: string
	targetBranchName?: string
	onClose: () => void
	onResolve?: (
		structure: MrStructure,
		node: DiffNode,
		resolution: Resolution,
		customValue?: unknown,
	) => Promise<void>
	readOnly?: boolean
}

export const MRDiffDrawer = ({
	open,
	structure,
	sourceBranchName,
	targetBranchName,
	onClose,
	onResolve,
	readOnly,
}: Props) => {
	const { t } = useAppContext()
	const [, force] = useReducer((x: number) => x + 1, 0)
	const [attrsCollapsed, setAttrsCollapsed] = useState(false)
	const [section, setSection] = useState<string | null>(null)
	const [lastKey, setLastKey] = useState<string | null>(null)
	const [savingPaths, setSavingPaths] = useState<Set<string>>(() => new Set())
	const [width, setWidth] = useState(() => {
		const v = parseInt(window.localStorage.getItem('metacroc_diffdrawer_w') || '', 10)
		return Number.isFinite(v) ? Math.min(drawerMaxW(), Math.max(DRAWER_MIN_W, v)) : DRAWER_DEFAULT_W
	})
	const [isDragging, setIsDragging] = useState(false)
	const [customPrompt, setCustomPrompt] = useState<{
		node: DiffNode
		value: string
	} | null>(null)

	useEffect(() => {
		window.localStorage.setItem('metacroc_diffdrawer_w', String(width))
	}, [width])

	useEffect(() => {
		if (!structure) return
		if (lastKey !== structure.key) {
			setLastKey(structure.key)
			setSection(null)
		}
	}, [structure, lastKey])

	const onDragStart = (ev: React.MouseEvent) => {
		ev.preventDefault()
		setIsDragging(true)
		const startX = ev.clientX
		const startWidth = width
		const maxW = drawerMaxW()
		const onMove = (mv: MouseEvent) => {
			const dx = mv.clientX - startX
			setWidth(Math.min(maxW, Math.max(DRAWER_MIN_W, startWidth - dx)))
		}
		const onUp = () => {
			setIsDragging(false)
			window.removeEventListener('mousemove', onMove)
			window.removeEventListener('mouseup', onUp)
		}
		window.addEventListener('mousemove', onMove)
		window.addEventListener('mouseup', onUp)
	}

	if (!structure) {
		return <Drawer anchor="right" open={false} onClose={onClose} />
	}

	const tree = buildTree(structure)
	const isConflictNode = (node: DiffNode) =>
		structure.conflictPaths
			? structure.conflictPaths.includes(node.path)
			: DiffEngine.CONFLICT_STATUSES.has(node.status)
	const unresolved = unresolvedCount(tree, structure.conflictPaths)
	const originalPaneLabel = targetBranchName
		? t('MERGE_REQUESTS_DIFF_ORIGINAL_LABEL', [targetBranchName])
		: t('MERGE_REQUESTS_DIFF_ORIGINAL')
	const newPaneLabel = sourceBranchName
		? t('MERGE_REQUESTS_DIFF_NEW_LABEL', [sourceBranchName])
		: t('MERGE_REQUESTS_DIFF_NEW')
	const bump = () => {
		force()
	}

	const saveResolution = async (
		node: DiffNode,
		key: Resolution,
		customValue?: unknown,
	) => {
		if (
			readOnly ||
			!isConflictNode(node) ||
			savingPaths.has(node.path)
		) {
			return
		}
		setSavingPaths((current) => new Set(current).add(node.path))
		try {
			await onResolve?.(structure, node, key, customValue)
			node.resolution = key
			if (key === 'custom') node.customValue = customValue
			bump()
		} catch {
			// The caller reports the API error; keep this conflict unresolved.
		} finally {
			setSavingPaths((current) => {
				const next = new Set(current)
				next.delete(node.path)
				return next
			})
		}
	}

	const setRes = (node: DiffNode, key: Resolution) => {
		if (readOnly || !isConflictNode(node)) return
		if (key === 'custom') {
			const cur =
				node.customValue != null
					? node.customValue
					: node.source != null
						? node.source
						: node.target
			setCustomPrompt({
				node,
				value:
					typeof cur === 'object'
						? JSON.stringify(cur)
						: String(cur == null ? '' : cur),
			})
			return
		}
		void saveResolution(node, key)
	}

	const applyCustomValue = (input: string) => {
		if (!customPrompt) return
		const { node } = customPrompt
		let v: unknown = input
		try {
			v = JSON.parse(input)
		} catch {
			/* keep string */
		}
		setCustomPrompt(null)
		void saveResolution(node, 'custom', v)
	}

	const resolveAll = async (choice: 'source' | 'target') => {
		const nodes = conflictNodes(tree, structure.conflictPaths)
		await Promise.all(nodes.map((node) => saveResolution(node, choice)))
	}

	const cols = collections(tree)
	const activeSection = (section && cols.find((c) => c.label === section)) || cols[0] || null

	const cell = (side: 'source' | 'target', node: DiffNode, type: Row['type']) => {
		// Entity rows only group the changed fields listed beneath them; rendering the
		// whole object here would just show a meaningless key count on each side.
		if (type === 'entity') {
			return (
				<Box
					sx={{
						px: '12px',
						py: '6px',
						minWidth: 0,
						borderLeft: '3px solid transparent',
						display: 'flex',
						alignItems: 'center',
					}}
				>
					<Box
						sx={{
							fontSize: '12px',
							fontWeight: 700,
							color: '#2A2A2A',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{node.displayLabel ?? node.label}
					</Box>
				</Box>
			)
		}
		const showingCustom = side === 'source' && node.resolution === 'custom'
		const v = showingCustom
			? node.customValue
			: side === 'source'
				? node.source
				: node.target
		const fam = isConflictNode(node) ? 'conflict' : statusFamily(node.status)
		const absent = !showingCustom && v === undefined
		const notChosen =
			!readOnly &&
			Boolean(node.resolution && node.resolution !== side && node.resolution !== 'custom')
		let bg = 'transparent'
		let bl = 'transparent'
		if (fam === 'conflict') {
			bg = '#FCEDEC'
			bl = '#C5221F'
		} else if (
			fam === 'added' &&
			((side === 'source' &&
				DiffEngine.ADDED_STATUSES.has(node.status) &&
				node.status !== 'added-tgt') ||
				(side === 'target' && node.status !== 'added-src'))
		) {
			bg = '#EAF6EE'
			bl = '#0F9D58'
		} else if (side === 'source' && node.status === 'modified-src') {
			bg = '#FDF3E3'
			bl = '#C26401'
		} else if (side === 'target' && node.status === 'modified-tgt') {
			bg = '#FDF3E3'
			bl = '#C26401'
		} else if (node.status === 'removed-src' && side === 'target') {
			bg = '#FDF3E3'
			bl = '#C26401'
		} else if (node.status === 'removed-tgt' && side === 'source') {
			bg = '#FDF3E3'
			bl = '#C26401'
		}
		// A whole entity appeared or disappeared, so spell out every field it holds
		// rather than promoting its code to a heading above the rest.
		const fields = type === 'block' && !absent ? entityFields(v) : []
		return (
			<Box
				sx={{
					px: '12px',
					py: '6px',
					minWidth: 0,
					borderLeft: `3px solid ${bl}`,
					bgcolor: bg,
					display: 'flex',
					flexDirection: 'column',
					gap: '1px',
					opacity: absent ? 0.4 : notChosen ? 0.5 : 1,
				}}
			>
				{fields.length ? null : (
					<Box
						sx={{
							fontSize: '10px',
							color: '#9A9A9A',
							fontWeight: 600,
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{node.displayLabel ?? node.label}
					</Box>
				)}
				{fields.length ? (
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'auto minmax(0, 1fr)',
							columnGap: '8px',
							rowGap: '1px',
							textDecoration: notChosen ? 'line-through' : 'none',
						}}
					>
						{fields.map((field) => (
							<Fragment key={field.label}>
								<Box sx={{ fontSize: '10.5px', color: '#7A7A7A' }}>{field.label}</Box>
								<Box
									sx={{
										fontSize: '11.5px',
										color: '#2A2A2A',
										fontFamily: 'ui-monospace, monospace',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{field.value}
								</Box>
							</Fragment>
						))}
					</Box>
				) : (
					<Box
						sx={{
							fontSize: '12px',
							color: absent ? '#B0B0B0' : '#2A2A2A',
							fontFamily: 'ui-monospace, monospace',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							textDecoration: notChosen ? 'line-through' : 'none',
						}}
					>
						{fmtValue(v)}
					</Box>
				)}
			</Box>
		)
	}

	const toggle = (node: DiffNode) => {
		const isConflict = isConflictNode(node)
		const btn = (key: 'source' | 'target' | 'custom', label: string, active: boolean) => (
			<Box
				key={key}
				onClick={(ev) => {
					ev.stopPropagation()
					setRes(node, key)
				}}
				sx={{
					px: '7px',
					py: '3px',
					fontSize: '10.5px',
					fontWeight: 700,
					cursor: readOnly || savingPaths.has(node.path) ? 'default' : 'pointer',
					borderRadius: '5px',
					border: '1px solid',
					whiteSpace: 'nowrap',
					...(active
						? { bgcolor: '#1F4954', color: '#fff', borderColor: '#1F4954' }
						: {
								bgcolor: '#fff',
								color: '#6A6A6A',
								borderColor: '#D8D8D8',
								'&:hover':
									readOnly || savingPaths.has(node.path)
										? {}
										: { borderColor: '#1F4954' },
							}),
				}}
			>
				{label}
			</Box>
		)
		return (
			<Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
				{btn('target', t('MERGE_REQUESTS_DIFF_TARGET'), node.resolution === 'target')}
				{btn(
					'custom',
					node.resolution === 'custom'
						? t('MERGE_REQUESTS_DIFF_CUSTOM_SET')
						: t('MERGE_REQUESTS_DIFF_CUSTOM'),
					node.resolution === 'custom',
				)}
				{btn('source', t('MERGE_REQUESTS_DIFF_SOURCE'), node.resolution === 'source')}
				{isConflict && !node.resolution ? (
					<Tooltip title={t('MERGE_REQUESTS_DIFF_UNRESOLVED_CONFLICT')} arrow>
						<Box sx={{ color: '#C5221F', display: 'flex' }}>
							<ErrorOutlineOutlinedIcon sx={{ fontSize: 14 }} />
						</Box>
					</Tooltip>
				) : null}
			</Box>
		)
	}

	const centerCell = (row: Row) => {
		const { node, type } = row
		const fam = isConflictNode(node) ? 'conflict' : statusFamily(node.status)
		const resolvedConflict = Boolean(node.resolution && fam === 'conflict')
		const dot = (
			<Box
				sx={{
					width: 16,
					height: 16,
					borderRadius: '50%',
					flex: '0 0 auto',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: '11px',
					fontWeight: 800,
					color: '#fff',
					bgcolor: resolvedConflict ? '#0F9D58' : FAM_COLOR[fam],
				}}
			>
				{resolvedConflict ? '✓' : FAM_GLYPH[fam]}
			</Box>
		)
		if (type === 'entity' || type === 'block') {
			const c = node.counts || { conflict: 0, changed: 0 }
			const label =
				type === 'block'
					? fam === 'added'
						? 'added'
						: 'removed'
					: c.conflict
						? `${c.conflict} conflict${c.conflict > 1 ? 's' : ''}`
						: `${c.changed} change${c.changed !== 1 ? 's' : ''}`
			return (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '6px',
						fontSize: '10px',
						color: '#9A9A9A',
						fontWeight: 600,
						textTransform: 'uppercase',
						letterSpacing: '.03em',
					}}
				>
					{dot}
					<span>{label}</span>
				</Box>
			)
		}
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
				{dot}
				{readOnly || fam !== 'conflict' ? null : toggle(node)}
			</Box>
		)
	}

	const paneHead = (label: string, color: string, bg: string) => (
		<Box
			sx={{
				height: HEAD,
				px: '14px',
				display: 'flex',
				alignItems: 'center',
				gap: '7px',
				fontSize: '10.5px',
				fontWeight: 700,
				letterSpacing: '.06em',
				textTransform: 'uppercase',
				color,
				bgcolor: bg,
				borderBottom: '1px solid #E4E4E4',
				position: 'sticky',
				top: 0,
				zIndex: 2,
			}}
		>
			<span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
		</Box>
	)

	const threePane = (rows: Row[], originalLabel: string, nextLabel: string, emptyText: string) => (
		<Box
			sx={{
				border: '1px solid #E4E4E4',
				borderRadius: '10px',
				overflow: 'hidden',
				bgcolor: '#fff',
			}}
		>
			{rows.length === 0 ? (
				<Box sx={{ p: '22px', textAlign: 'center', color: '#B0B0B0', fontSize: '12.5px' }}>{emptyText}</Box>
			) : (
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: readOnly
							? 'minmax(0,1fr) 88px minmax(0,1fr)'
							: 'minmax(0,1fr) 250px minmax(0,1fr)',
					}}
				>
					{paneHead(originalLabel, '#C5221F', '#FDF3F2')}
					<Box
						sx={{
							height: HEAD,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: '10.5px',
							fontWeight: 700,
							letterSpacing: '.06em',
							textTransform: 'uppercase',
							color: '#555',
							bgcolor: '#FAFAFB',
							borderBottom: '1px solid #E4E4E4',
							borderLeft: '1px solid #EEE',
							borderRight: '1px solid #EEE',
							position: 'sticky',
							top: 0,
							zIndex: 2,
						}}
					>
						{readOnly ? '' : t('MERGE_REQUESTS_DIFF_RESOLVE')}
					</Box>
					{paneHead(nextLabel, '#0F9D58', '#F2FAF5')}
					{rows.flatMap((row, i) => {
						const rowBg = row.type === 'entity' || row.type === 'block' ? '#F7F8FA' : i % 2 ? '#FCFCFD' : '#fff'
						return [
							<Box key={`l${i}`} sx={{ borderTop: '1px solid #F0F0F0', bgcolor: rowBg }}>
								{cell('target', row.node, row.type)}
							</Box>,
							<Box
								key={`c${i}`}
								sx={{
									borderTop: '1px solid #F0F0F0',
									borderLeft: '1px solid #F0F0F0',
									borderRight: '1px solid #F0F0F0',
									bgcolor: row.type === 'entity' || row.type === 'block' ? '#F7F8FA' : '#FAFAFB',
									display: 'flex',
									alignItems: 'center',
									px: '8px',
								}}
							>
								<Box sx={{ width: '100%' }}>{centerCell(row)}</Box>
							</Box>,
							<Box key={`r${i}`} sx={{ borderTop: '1px solid #F0F0F0', bgcolor: rowBg }}>
								{cell('source', row.node, row.type)}
							</Box>,
						]
					})}
				</Box>
			)}
		</Box>
	)

	const attrRows = topAttrRows(tree)

	return (
		<>
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{
				style: { width: Math.min(width, drawerMaxW()), maxWidth: '96vw' },
				sx: { bgcolor: '#F7F7F7', transition: isDragging ? 'none' : undefined },
			}}
		>
			<Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
				<Box
					onMouseDown={onDragStart}
					sx={{
						position: 'absolute',
						top: 0,
						bottom: 0,
						left: -7,
						width: 14,
						cursor: 'col-resize',
						zIndex: 10,
						'&:hover .mc-diff-edge-line': { bgcolor: 'rgba(31,73,84,0.18)' },
					}}
				>
					<Box
						className="mc-diff-edge-line"
						sx={{
							position: 'absolute',
							top: 0,
							bottom: 0,
							left: 4,
							width: 6,
							bgcolor: isDragging ? 'rgba(31,73,84,0.18)' : 'transparent',
							transition: 'background .1s',
							pointerEvents: 'none',
						}}
					/>
				</Box>

				<Box
					sx={{
						flex: '0 0 auto',
						px: '18px',
						py: '13px',
						bgcolor: '#fff',
						borderBottom: '1px solid #E4E4E4',
						display: 'flex',
						alignItems: 'center',
						gap: '12px',
					}}
				>
					<Box sx={{ color: '#1F4954', display: 'flex' }}>
						<AccountTreeOutlinedIcon sx={{ fontSize: 18 }} />
					</Box>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1F4954' }}>
							{structure.label}
						</Typography>
						<Typography sx={{ fontSize: '11px', color: '#9A9A9A' }}>
							{t('MERGE_REQUESTS_DIFF_SUBTITLE')}
						</Typography>
					</Box>
					{readOnly ? null : unresolved ? (
						<Box
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: '5px',
								px: '9px',
								py: '4px',
								borderRadius: '7px',
								bgcolor: '#FDE7E6',
								color: '#C5221F',
								fontSize: '11px',
								fontWeight: 700,
							}}
						>
							<ErrorOutlineOutlinedIcon sx={{ fontSize: 13 }} />
							{t('MERGE_REQUESTS_UNRESOLVED_COUNT', [String(unresolved)])}
						</Box>
					) : (
						<Box
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: '5px',
								px: '9px',
								py: '4px',
								borderRadius: '7px',
								bgcolor: '#E6F4EA',
								color: '#0F9D58',
								fontSize: '11px',
								fontWeight: 700,
							}}
						>
							<CheckOutlinedIcon sx={{ fontSize: 13 }} />
							{t('MERGE_REQUESTS_DIFF_ALL_RESOLVED')}
						</Box>
					)}
					<Box
						onClick={onClose}
						sx={{
							display: 'flex',
							color: '#9A9A9A',
							cursor: 'pointer',
							p: '5px',
							borderRadius: '6px',
							'&:hover': { bgcolor: '#F0F0F0', color: '#2A2A2A' },
						}}
					>
						<CloseOutlinedIcon sx={{ fontSize: 18 }} />
					</Box>
				</Box>

				{!readOnly ? (
					<Box
						sx={{
							flex: '0 0 auto',
							px: '18px',
							py: '9px',
							bgcolor: '#fff',
							borderBottom: '1px solid #ECECEC',
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
						}}
					>
						<Box sx={{ fontSize: '11px', color: '#9A9A9A', fontWeight: 600, mr: '2px' }}>
							{t('MERGE_REQUESTS_DIFF_BULK')}
						</Box>
						<Button
							size="small"
							variant="outlined"
							disabled={savingPaths.size > 0}
							onClick={() => void resolveAll('target')}
							sx={{
								textTransform: 'none',
								borderRadius: '7px',
								borderColor: '#D8D8D8',
								color: '#52666C',
								fontSize: '11.5px',
							}}
						>
							{t('MERGE_REQUESTS_DIFF_ACCEPT_ALL_TARGET')}
						</Button>
						<Button
							size="small"
							variant="outlined"
							disabled={savingPaths.size > 0}
							onClick={() => void resolveAll('source')}
							sx={{
								textTransform: 'none',
								borderRadius: '7px',
								borderColor: '#D8D8D8',
								color: '#52666C',
								fontSize: '11.5px',
							}}
						>
							{t('MERGE_REQUESTS_DIFF_ACCEPT_ALL_SOURCE')}
						</Button>
					</Box>
				) : null}

				<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
					<Box sx={{ flex: '0 0 auto', m: '14px 14px 0' }}>
						<Box
							onClick={() => setAttrsCollapsed(!attrsCollapsed)}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								cursor: 'pointer',
								px: '4px',
								py: '4px',
								mb: '8px',
							}}
						>
							{attrsCollapsed ? (
								<ChevronRightOutlinedIcon sx={{ fontSize: 14, color: '#9A9A9A' }} />
							) : (
								<ExpandMoreOutlinedIcon sx={{ fontSize: 14, color: '#9A9A9A' }} />
							)}
							<Box
								sx={{
									fontSize: '11px',
									fontWeight: 700,
									letterSpacing: '.05em',
									textTransform: 'uppercase',
									color: '#1F4954',
								}}
							>
								{t('MERGE_REQUESTS_DIFF_BASIC_ATTRS')}
							</Box>
							<Box sx={{ fontSize: '11px', color: '#9A9A9A' }}>
								{t('MERGE_REQUESTS_DIFF_CHANGED_COUNT', [String(attrRows.length)])}
							</Box>
						</Box>
						{!attrsCollapsed
							? threePane(
									attrRows,
									originalPaneLabel,
									newPaneLabel,
									t('MERGE_REQUESTS_DIFF_NO_TABLE_ATTR'),
								)
							: null}
					</Box>

					{cols.length > 0 ? (
						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								display: 'flex',
								flexDirection: 'column',
								m: '10px 14px 14px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									gap: '2px',
									borderBottom: '1px solid #E4E4E4',
									flex: '0 0 auto',
								}}
							>
								{cols.map((c) => {
									const active = activeSection && activeSection.label === c.label
									const cnt = c.counts || { conflict: 0, changed: 0 }
									return (
										<Box
											key={c.label}
											onClick={() => setSection(c.label)}
											sx={{
												px: '14px',
												py: '9px',
												cursor: 'pointer',
												fontSize: '12.5px',
												fontWeight: 600,
												color: active ? '#1F4954' : '#7A7A7A',
												borderBottom: `2px solid ${active ? '#F9423A' : 'transparent'}`,
												display: 'flex',
												alignItems: 'center',
												gap: '6px',
												whiteSpace: 'nowrap',
												'&:hover': { color: '#1F4954' },
											}}
										>
											<span>{c.label}</span>
											<span style={{ color: '#B0B0B0', fontWeight: 500 }}>
												({c.children ? c.children.length : 0})
											</span>
											{cnt.conflict ? (
												<Box
													sx={{
														fontSize: '9.5px',
														fontWeight: 800,
														color: '#C5221F',
														bgcolor: '#FDE7E6',
														borderRadius: '999px',
														px: '6px',
														minWidth: 16,
														textAlign: 'center',
													}}
												>
													{cnt.conflict}
												</Box>
											) : cnt.changed ? (
												<Box
													sx={{
														fontSize: '9.5px',
														fontWeight: 800,
														color: '#C26401',
														bgcolor: '#FEF2DC',
														borderRadius: '999px',
														px: '6px',
														minWidth: 16,
														textAlign: 'center',
													}}
												>
													{cnt.changed}
												</Box>
											) : null}
										</Box>
									)
								})}
							</Box>
							<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pt: '12px' }}>
								{activeSection
									? threePane(
											collectionRows(activeSection),
											originalPaneLabel,
											newPaneLabel,
											t('MERGE_REQUESTS_DIFF_NO_COLLECTION_CHANGES'),
										)
									: null}
							</Box>
						</Box>
					) : null}

					{attrRows.length === 0 && cols.length === 0 ? (
						<Box sx={{ p: '40px', textAlign: 'center', color: '#B0B0B0', fontSize: '13px' }}>
							{t('MERGE_REQUESTS_DIFF_NO_CHANGES')}
						</Box>
					) : null}
				</Box>

				<Box
					sx={{
						flex: '0 0 auto',
						px: '18px',
						py: '11px',
						bgcolor: '#fff',
						borderTop: '1px solid #E4E4E4',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '10px',
					}}
				>
					<Button
						variant="contained"
						disableElevation
						onClick={onClose}
						sx={{ borderRadius: '8px', px: '20px' }}
					>
						{t('DONE')}
					</Button>
				</Box>
			</Box>
		</Drawer>
		<PromptDialog
			open={!!customPrompt}
			title={
				customPrompt
					? t('MERGE_REQUESTS_DIFF_CUSTOM_VALUE_FOR', [customPrompt.node.label])
					: t('MERGE_REQUESTS_DIFF_CUSTOM_VALUE')
			}
			defaultValue={customPrompt?.value ?? ''}
			onCancel={() => setCustomPrompt(null)}
			onConfirm={applyCustomValue}
		/>
		</>
	)
}
